import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, styled, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import validator from 'validator';
import ContactUsMessage from './ContactUsMessage';
import Logo from '../../../Assets/BugLogo.png';

const FormContainer = styled(Box)(({ theme }) => ({
  maxWidth: '600px',
  width: '100%',
  margin: '40px 20px',
  fontFamily: 'Arial, sans-serif',
  color: '#333',
  lineHeight: 1.6,
  border: '1px solid #ccc',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: '#f9f9f9',
  padding: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    margin: '20px 10px',
  },
}));

const ContactUs = () => {
  const [name, setName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [textAgree, setTextAgree] = useState(false);

  const [nameError, setNameError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [messageError, setMessageError] = useState('');

  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (email) => {
    return validator.isEmail(String(email));
  };

  const validateMobile = (mobile) => {
    return validator.isMobilePhone(String(mobile), 'en-US');
  };

  useEffect(() => {
    const isNameValid = name.trim() !== '';
    const isMobileValid = mobileNumber.trim() !== '' && validateMobile(mobileNumber.trim());
    const isEmailValid = email.trim() !== '' && validateEmail(email.trim());
    const isMessageValid = message.trim() !== '';

    setIsFormValid(isNameValid && isMobileValid && isEmailValid && isMessageValid);
  }, [name, mobileNumber, email, message]);

  const handleNameChange = (e) => {
    setName(e.target.value);
    setNameError('');
  };

  const handleMobileChange = (e) => {
    setMobileNumber(e.target.value);
    setMobileError('');
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError('');
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    setMessageError('');
  };

  const handleNameBlur = () => {
    if (name.trim() === '') setNameError('Name cannot be empty');
    else setNameError('');
  };

  const handleMobileBlur = () => {
    const trimmedMobile = mobileNumber.trim();
    if (trimmedMobile === '') setMobileError('Mobile Number cannot be empty');
    else if (!validateMobile(trimmedMobile)) setMobileError('Please enter a valid US phone number');
    else setMobileError('');
  };

  const handleEmailBlur = () => {
    const trimmedEmail = email.trim();
    if (trimmedEmail === '') setEmailError('Email Address cannot be empty');
    else if (!validateEmail(trimmedEmail)) setEmailError('Please enter a valid email address');
    else setEmailError('');
  };

  const handleMessageBlur = () => {
    if (message.trim() === '') setMessageError('Message cannot be empty');
    else setMessageError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    handleNameBlur();
    handleMobileBlur();
    handleEmailBlur();
    handleMessageBlur();

    const canSubmit =
      name.trim() !== '' &&
      !nameError &&
      mobileNumber.trim() !== '' &&
      validateMobile(mobileNumber.trim()) &&
      !mobileError &&
      email.trim() !== '' &&
      validateEmail(email.trim()) &&
      !emailError &&
      message.trim() !== '' &&
      !messageError;

    if (canSubmit) {
      setIsLoading(true);
      setSubmissionError(null);

      try {
        const payload = {
          name: name.trim(),
          mobileNumber: mobileNumber.trim(),
          emailAddress: email.trim(),
          message: message.trim(),
          textAgree: textAgree,
        };

        await ContactUsMessage(payload);
        setIsSubmitted(true);
      } catch (error) {
        // console.error('Submission failed:', error);
        setSubmissionError('Message submission unsuccessful, please try again');
        setIsLoading(false);
      }
    } else {
      // console.log('Submit blocked due to errors or incomplete fields.');
    }
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          textAlign: 'center',
        }}
      >
        <FormContainer>
          <Typography variant='h5' sx={{ py: 4 }}>
            Your message was sent successfully. Thank you for contacting us!
          </Typography>
        </FormContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: '20px', md: '40px' },
          mx: { xs: '20px', md: '40px' },
        }}
      >
        <img src={Logo} alt='Logo' style={{ maxWidth: '300px', height: 'auto' }} />
      </Box>

      <FormContainer>
        <Typography variant='h4' sx={{ pt: 3, pb: 1, fontWeight: 'bold', textAlign: 'center' }}>
          Contact Us
        </Typography>
        <Typography variant='subtitle1' sx={{ pb: 4, textAlign: 'center' }}>
          We love hearing from you! Your feedback is greatly appreciated.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label='Name'
              variant='outlined'
              fullWidth
              value={name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              required
              error={!!nameError}
              helperText={nameError}
              disabled={isLoading}
            />
            <TextField
              label='Mobile Number'
              variant='outlined'
              fullWidth
              type='tel'
              value={mobileNumber}
              onChange={handleMobileChange}
              onBlur={handleMobileBlur}
              required
              error={!!mobileError}
              helperText={mobileError}
              disabled={isLoading}
            />
            <TextField
              label='Email Address'
              variant='outlined'
              fullWidth
              type='text'
              inputMode='email'
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              required
              error={!!emailError}
              helperText={emailError}
              disabled={isLoading}
            />

            <TextField
              label='Message'
              variant='outlined'
              fullWidth
              multiline
              rows={4}
              value={message}
              onChange={handleMessageChange}
              onBlur={handleMessageBlur}
              required
              error={!!messageError}
              helperText={messageError}
              disabled={isLoading}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={textAgree}
                  onChange={(e) => setTextAgree(e.target.checked)}
                  name='textAgree'
                  sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                />
              }
              label={
                <Typography variant='subtitle2' align='justify' sx={{ color: '#333' }}>
                  By checking this box, you agree to receive recurring automated promotional and
                  personalized marketing text messages (e.g. cart reminders) from Sports South at
                  the cell number listed on this form. Message frequency varies. Consent is not a
                  condition of any purchase. Reply HELP for help and STOP to cancel. Msg frequency
                  varies. Msg & data rates may apply. View Terms & Privacy. Firearms that are not
                  legally available in all 50 states will not be advertised via SMS.
                </Typography>
              }
              sx={{ mt: 1 }}
            />

            {submissionError && (
              <Typography color='error' sx={{ textAlign: 'center', mt: -1, mb: -2 }}>
                {submissionError}
              </Typography>
            )}

            <Button
              type='submit'
              variant='contained'
              color='primary'
              size='large'
              sx={{ mt: 1, py: 1.5 }}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Submit'}
            </Button>
          </Box>
        </form>
      </FormContainer>
    </Box>
  );
};

export default ContactUs;
